"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Predicate = exports.defaultFromStringConfig = exports.defaultToStringConfig = void 0;
const class_transformer_1 = require("class-transformer");
const MinifiedPredicate_1 = __importDefault(require("./MinifiedPredicate"));
const PredicateTree_1 = __importDefault(require("./PredicateTree"));
const PredicateStringParser_1 = require("./string-parser/PredicateStringParser");
function getDefaultConfig() {
    return {
        type: 'COMPLEX',
        isNegated: false,
    };
}
exports.defaultToStringConfig = {
    onlyValues: false,
};
exports.defaultFromStringConfig = {
    onlyValues: false,
    defaultFilterCriteria: 'id',
    defaultPredicateDefinition: 'IS',
};
// noinspection TypeScriptFieldCanBeMadeReadonly,JSUnusedGlobalSymbols
class Predicate extends PredicateTree_1.default {
    /**
     * Constructs a new predicate node. Optionally with children.
     * Default config: { type: 'COMPLEX', operator: 'AND', negated: false } (can be overwritten with constructor argument)
     * @param predicateConfig PredicateConfig
     */
    constructor(predicateConfig = getDefaultConfig()) {
        var _a;
        const config = Object.assign(Object.assign({}, getDefaultConfig()), predicateConfig);
        const mapToPredicate = (child) => {
            var _a;
            // @ts-ignore
            child.children = (_a = child.children) === null || _a === void 0 ? void 0 : _a.map(mapToPredicate);
            if (child instanceof Predicate) {
                return child;
            }
            return new Predicate(child);
        };
        config.children = (_a = config.children) === null || _a === void 0 ? void 0 : _a.map(mapToPredicate);
        super(config);
        this.name = config.name;
        this.type = config.type;
        this.isNegated = config.isNegated;
        this.operator = config.operator;
        this.filterCriteria = config.filterCriteria;
        this.predicateDefinition = config.predicateDefinition;
        this.parameterValue = config.parameterValue;
        this.toStringConfig = config.toStringConfig;
    }
    /**
     * Factory method
     * Parses predicate tree from minified json
     * Reverse side: toMinifiedJsonString()
     */
    static fromMinifiedJsonString(minifiedString) {
        const minifiedPredicate = (0, class_transformer_1.deserialize)(MinifiedPredicate_1.default, minifiedString, { groups: ['unMinify'] });
        const predicate = (0, class_transformer_1.plainToClass)(Predicate, minifiedPredicate, {
            targetMaps: [
                {
                    target: Predicate,
                    properties: { children: Predicate },
                }
            ]
        });
        predicate.updateParentProperties();
        return predicate;
    }
    /**
     * @deprecated Use fromMinifiedJsonString() instead. This method incorrectly represents the return type. Next major version will have the following signatures:
     *  - fromMinifiedJsonString(string): Predicate
     *  - fromMinifiedJson(object): Predicate
     */
    static fromMinifiedJson(minifiedString) {
        return this.fromMinifiedJsonString(minifiedString);
    }
    /**
     * Factory method
     * Parses predicate tree from json representation
     * Reverse side: toJson()
     * @param predicateJson
     */
    static fromJson(predicateJson) {
        const predicate = (0, class_transformer_1.plainToClass)(Predicate, predicateJson, {
            targetMaps: [
                {
                    target: Predicate,
                    properties: { children: Predicate },
                }
            ]
        });
        predicate.updateParentProperties();
        return predicate;
    }
    /**
     * Factory method
     * Parses predicate tree from string representation
     * Reverse side: toString()
     * @param predicateString String representation of the predicate model. For example:
     *  - "id IS 1"
     *  - "1 OR 2" (requires onlyValues: true in fromStringConfig)
     * @param fromStringConfig PredicateFromStringConfig
     */
    static fromString(predicateString, fromStringConfig = exports.defaultFromStringConfig) {
        const parser = new PredicateStringParser_1.PredicateStringParser();
        return parser.fromString(predicateString, fromStringConfig);
    }
    clone() {
        return Predicate.fromJson(this.toJson());
    }
    toMinifiedJsonString() {
        const minifiedPredicate = (0, class_transformer_1.plainToClass)(MinifiedPredicate_1.default, this, {
            excludeExtraneousValues: true,
            ignoreDecorators: true,
            enableCircularCheck: true
        });
        return (0, class_transformer_1.serialize)(minifiedPredicate, { groups: ['minify'] });
    }
    /**
     * Returns minified JSON string for predicate tree.
     * Also see reverse side: fromMinifiedJson()
     * @deprecated Use toMinifiedJsonString() instead. This method incorrectly represents the return type. Next major version will have the following signatures:
     *  - toMinifiedJsonString(): string
     *  - toMinifiedJson(): object
     */
    toMinifiedJson() {
        return this.toMinifiedJsonString();
    }
    toJson() {
        return (0, class_transformer_1.classToPlain)(this.withoutCircularReferences());
    }
    /**
     * Returns human-readable string for whole predicate tree.
     * Example output: "name IS_NOT some_name OR (state IS deleted AND id GREATER_THAN 10)"
     */
    toString(config = {}) {
        const toStringConfig = Object.assign(Object.assign(Object.assign({}, exports.defaultToStringConfig), this.toStringConfig), config);
        let negatedPrefix = '';
        if (this.isNegated) {
            negatedPrefix = 'NOT ';
        }
        if (this.isAtomic()) {
            let valueString = JSON.stringify(this.parameterValue);
            if (toStringConfig.getParameterValueLabel && typeof toStringConfig.getParameterValueLabel === 'function') {
                valueString = toStringConfig.getParameterValueLabel(this);
            }
            if (toStringConfig.onlyValues) {
                return negatedPrefix + valueString;
            }
            return `${negatedPrefix}${this.name || this.filterCriteria} ${this.predicateDefinition} ${valueString}`;
        }
        let childrenString = '';
        this.children.forEach((childPredicate, index) => {
            childrenString += childPredicate.toString(config);
            const nextChild = this.children[index + 1];
            if (nextChild) {
                childrenString += ` ${this.operator} `;
            }
        });
        if (this.hasMultipleChildren() && !this.isRoot()) {
            childrenString = `( ${childrenString} )`;
            if (this.isNegated) {
                childrenString = `NOT ${childrenString}`;
            }
        }
        if (this.isNegated && this.children.length === 1) {
            childrenString = `${negatedPrefix}${childrenString}`;
        }
        return childrenString;
    }
    getAtomicPredicates() {
        const atomicPredicates = [];
        this.traverseTreeBottomToTop((predicate) => {
            if (predicate.type === 'ATOMIC') {
                atomicPredicates.push(predicate);
            }
        });
        return atomicPredicates;
    }
    getParameterValuesFlat() {
        return this.getAtomicPredicates().map(predicate => predicate.parameterValue);
    }
    containsAtomic() {
        return this.getAtomicPredicates().length !== 0;
    }
    forEachAtomic(callback) {
        this.getAtomicPredicates().forEach(callback);
    }
    isComplex() {
        return this.type === 'COMPLEX';
    }
    isAtomic() {
        return this.type === 'ATOMIC';
    }
    setToStringConfig(toStringConfig) {
        this.toStringConfig = toStringConfig;
    }
}
exports.Predicate = Predicate;
//# sourceMappingURL=Predicate.js.map